import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { MapBlock } from '#components/document-viewer/map/map-block';
import {
  mapConfigToEditValues,
  mapEditValuesToConfig,
  normalizeMapConfig,
  stringifyMapConfig,
} from '#components/document-viewer/map/map-config';

import { MarkdownCustomBlockActions } from './markdown-custom-block-actions';
import {
  NP_MAP_TAG,
  parseMarkdownMapConfig,
  serializeMarkdownMapBlock,
  serializeMarkdownMapInnerConfig,
  type MarkdownMapEditRequest,
} from './markdown-map';

interface MarkdownMapNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

function normalizeMapAttrs(attrs: unknown) {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};

  return {
    data: normalizeMapConfig(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MarkdownMapNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownMapNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeMapAttrs(node.attrs);

  const handleEdit = () => {
    context.onRequestMapEdit?.({
      mode: 'edit',
      initialValues: mapConfigToEditValues(attrs.data),
      onCommit: async (next) => {
        const normalized = mapEditValuesToConfig(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyMapConfig(normalized),
          parseError: null,
        });
      },
    } satisfies MarkdownMapEditRequest);
  };

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-map-node markdown-map-node--selected'
          : 'markdown-map-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <MarkdownCustomBlockActions
        editLabel="Edit map block"
        onEdit={context.onRequestMapEdit ? handleEdit : undefined}
      >
        <MapBlock config={attrs.data} parseError={attrs.parseError} />
      </MarkdownCustomBlockActions>
    </NodeViewWrapper>
  );
}

export const MarkdownMapNode = Node.create<MarkdownMapNodeOptions>({
  name: 'npMap',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeMapConfig({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_MAP_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownMapConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeMapAttrs(HTMLAttributes);
    return [NP_MAP_TAG, serializeMarkdownMapInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeMapAttrs(node?.attrs);
    return serializeMarkdownMapBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownMapNodeView);
  },
});
